// ============================================================
// MyGene — MetaPanel
// Primitives for the left context rail of a 3-column detail
// drawer (treatment plan, lab group processing).
//
// The rail is a dense, read-only column: collapsible section
// headers with a disclosure chevron, then label/value rows.
// Collapse state persists to localStorage per storage key.
//
// Usage:
//
//   const [collapsed, toggle] = useMetaPanelCollapse("mg-labgroup-collapsed");
//
//   <MetaPanel>
//     <MetaSection title="Group" open={!collapsed.group} onToggle={() => toggle("group")}>
//       <MetaRow label="Status" value={<StatusTag status={status} />} />
//       <MetaRow label="Technician" value="A. Wisely" />
//     </MetaSection>
//     <MetaDivider />
//     <MetaSection title="Samples" count={24} open={!collapsed.samples} onToggle={() => toggle("samples")}>
//       <MetaListItem primary="S-1001" secondary="Received 4 Jun" />
//       <MetaEmptyRow>None recorded</MetaEmptyRow>
//     </MetaSection>
//   </MetaPanel>
// ============================================================

import React, { useCallback, useState } from "react";
import { RightOutlined } from "@ant-design/icons";

// ── Collapse persistence ──────────────────────────────────────

function readCollapsedState(storageKey: string): Record<string, boolean> {
	if (typeof window === "undefined") return {};
	try {
		const raw = window.localStorage.getItem(storageKey);
		return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
	} catch {
		return {};
	}
}

/**
 * Collapse state for a MetaPanel, persisted under `storageKey`.
 * Returns the map of collapsed section keys and a toggle for one key.
 */
export function useMetaPanelCollapse(
	storageKey: string,
): [Record<string, boolean>, (key: string) => void] {
	const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
		readCollapsedState(storageKey),
	);

	const toggle = useCallback(
		(key: string) => {
			setCollapsed((prev) => {
				const next = { ...prev, [key]: !prev[key] };
				if (typeof window !== "undefined") {
					try {
						window.localStorage.setItem(storageKey, JSON.stringify(next));
					} catch {
						/* ignore write errors — collapse state is a convenience, not data */
					}
				}
				return next;
			});
		},
		[storageKey],
	);

	return [collapsed, toggle];
}

// ── Styles ────────────────────────────────────────────────────
// Kept in a stylesheet rather than inline so hover, focus-visible
// and reduced-motion can all be expressed.

const META_PANEL_STYLES = `
.mg-meta-section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--mg-sp-4);
	width: 100%;
	padding: 6px 10px 4px;
	border: 0;
	border-bottom: 1px solid var(--mg-border);
	background: var(--mg-bg-subtle);
	color: var(--mg-text-secondary);
	font-size: var(--mg-fs-xxs);
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	text-align: left;
	cursor: pointer;
	transition: color 0.15s ease;
}
.mg-meta-section-header:hover { color: var(--mg-text); }
.mg-meta-section-header:focus-visible {
	outline: 2px solid var(--mg-primary);
	outline-offset: -2px;
}
.mg-meta-section-chevron {
	flex-shrink: 0;
	font-size: 9px;
	transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}
.mg-meta-section-chevron[data-open="true"] { transform: rotate(90deg); }
.mg-meta-section-body {
	overflow: hidden;
	transition: max-height 0.22s cubic-bezier(0.16, 1, 0.3, 1),
		opacity 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}
.mg-meta-item-clickable { cursor: pointer; transition: background 0.15s ease; }
.mg-meta-item-clickable:hover { background: var(--mg-primary-bg); }
.mg-meta-item-clickable:focus-visible {
	outline: 2px solid var(--mg-primary);
	outline-offset: -2px;
}
@media (prefers-reduced-motion: reduce) {
	.mg-meta-section-chevron,
	.mg-meta-section-body,
	.mg-meta-item-clickable { transition: none; }
}
`;

const MetaPanelStyles: React.FC = () => <style>{META_PANEL_STYLES}</style>;

// ── Panel shell ───────────────────────────────────────────────

interface MetaPanelProps {
	children: React.ReactNode;
	/** Rail width in px. Defaults to 210. */
	width?: number;
}

/** The rail itself — a fixed-width, scrollable left column. */
export const MetaPanel: React.FC<MetaPanelProps> = ({ children, width = 210 }) => (
	<div
		style={{
			width,
			flexShrink: 0,
			height: "100%",
			overflowY: "auto",
			borderRight: "1px solid var(--mg-border)",
			fontSize: 12,
		}}
	>
		<MetaPanelStyles />
		{children}
	</div>
);

// ── Section ───────────────────────────────────────────────────

interface MetaSectionProps {
	title: string;
	/** Appended to the title as "(n)" when greater than zero. */
	count?: number;
	open: boolean;
	onToggle: () => void;
	children: React.ReactNode;
}

export const MetaSection: React.FC<MetaSectionProps> = ({
	title,
	count,
	open,
	onToggle,
	children,
}) => (
	<>
		<button
			type="button"
			className="mg-meta-section-header"
			aria-expanded={open}
			onClick={onToggle}
		>
			<span>
				{title}
				{count != null && count > 0 ? ` (${count})` : ""}
			</span>
			<RightOutlined className="mg-meta-section-chevron" data-open={open} />
		</button>
		<div
			className="mg-meta-section-body"
			style={{ maxHeight: open ? 900 : 0, opacity: open ? 1 : 0 }}
		>
			{children}
		</div>
	</>
);

// ── Rows ──────────────────────────────────────────────────────

interface MetaRowProps {
	label: string;
	value: React.ReactNode;
}

/** Label above value — the default read-only row. */
export const MetaRow: React.FC<MetaRowProps> = ({ label, value }) => (
	<div style={{ padding: "4px 10px", borderBottom: "1px solid var(--mg-border)", fontSize: 12 }}>
		<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginBottom: 1 }}>{label}</div>
		<div style={{ color: "var(--mg-text)", fontWeight: 500, wordBreak: "break-word" }}>{value}</div>
	</div>
);

interface MetaListItemProps {
	primary: React.ReactNode;
	secondary?: React.ReactNode;
	/** Makes the row interactive — adds hover, focus ring and keyboard activation. */
	onClick?: () => void;
}

/** A compact list row: primary line plus optional muted secondary line. */
export const MetaListItem: React.FC<MetaListItemProps> = ({ primary, secondary, onClick }) => (
	<div
		className={onClick ? "mg-meta-item-clickable" : undefined}
		role={onClick ? "button" : undefined}
		tabIndex={onClick ? 0 : undefined}
		onClick={onClick}
		onKeyDown={
			onClick
				? (e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							onClick();
						}
				  }
				: undefined
		}
		style={{ padding: "4px 10px", borderBottom: "1px solid var(--mg-border)", fontSize: 12 }}
	>
		<div style={{ color: "var(--mg-text)", fontWeight: 500, wordBreak: "break-word" }}>{primary}</div>
		{secondary != null && secondary !== "" && (
			<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", wordBreak: "break-word" }}>
				{secondary}
			</div>
		)}
	</div>
);

/** Muted row for "none recorded" / "loading" states inside a section. */
export const MetaEmptyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div
		style={{
			padding: "4px 10px",
			borderBottom: "1px solid var(--mg-border)",
			fontSize: 11,
			color: "var(--mg-text-disabled)",
		}}
	>
		{children}
	</div>
);

/** Hairline separator between sections. */
export const MetaDivider: React.FC = () => (
	<div style={{ height: 1, background: "var(--mg-border)", margin: "4px 0" }} />
);

export default MetaPanel;
